import { useEffect, useState } from 'react';
import { api } from './api.js';

interface Panel { id: string; drawn_at: string; lab_name: string | null; extraction_status: string; marker_count: string; flagged_count: string; drawn_relative_to_dose: string | null; trained_within_48h: boolean | null }
interface Flag { marker_code: string; label: string; value: string; unit: string; flag: string; why_it_matters_md: string | null; drawn_at: string }

export function Bloods(): JSX.Element {
  const [panels, setPanels] = useState<Panel[]>([]);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [timing, setTiming] = useState('trough');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = (): void => {
    void api<Panel[]>('/labs/panels').then(setPanels).catch(() => undefined);
    void api<Flag[]>('/labs/flags').then(setFlags).catch(() => undefined);
  };
  useEffect(reload, []);

  async function upload(): Promise<void> {
    if (!file) return;
    setBusy(true); setMsg(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('drawnRelativeToDose', timing);
    const raw = window.sessionStorage.getItem('orbit-health.session');
    const token = raw ? (JSON.parse(raw) as { token: string }).token : '';
    const res = await fetch('/api/labs/panels', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const body = await res.json().catch(() => ({}));
    setBusy(false);
    setMsg(res.ok ? `Read ${body.markersFound} markers.${body.timingNote ? ' ' + body.timingNote : ''}` : `Failed: ${body.message ?? body.error}`);
    reload();
  }

  return (
    <>
      {flags.length > 0 && (
        <div className="card warn">
          <h2>Out of range on your latest panel</h2>
          {flags.map((f) => (
            <div key={f.marker_code} className="subcard">
              <div className="crow">
                <strong>{f.label}</strong>
                <span className={f.flag.startsWith('critical') ? 'bad' : ''}>{Number(f.value)} {f.unit} · {f.flag.replace('_', ' ')}</span>
              </div>
              {f.why_it_matters_md && <p className="small">{f.why_it_matters_md}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <h2>Upload a panel</h2>
        <label htmlFor="lf">Lab result (image or PDF)</label>
        <input id="lf" type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <label htmlFor="tm">When was it drawn relative to your last dose?</label>
        <select id="tm" value={timing} onChange={(e) => setTiming(e.target.value)}>
          <option value="trough">Trough, just before the next dose</option>
          <option value="mid">Somewhere in the middle</option>
          <option value="peak">Soon after a dose</option>
          <option value="unknown">Not sure</option>
        </select>
        <p className="muted small">
          This matters. Testosterone at trough and at peak are not comparable, so a trend line
          built from mixed timings is misleading.
        </p>
        <button type="button" disabled={!file || busy} onClick={() => void upload()}>
          {busy ? 'Reading…' : 'Upload and extract'}
        </button>
        {msg && <p className="muted small">{msg}</p>}
      </div>

      <div className="card">
        <h2>Panels</h2>
        {panels.length === 0 && <p className="muted">No bloods on record yet.</p>}
        <table className="tbl">
          <thead><tr><th>Drawn</th><th>Lab</th><th>Markers</th><th>Flagged</th><th>Timing</th></tr></thead>
          <tbody>
            {panels.map((p) => (
              <tr key={p.id}>
                <td>{new Date(p.drawn_at).toLocaleDateString()}</td>
                <td className="muted">{p.lab_name ?? '-'}</td>
                <td>{p.marker_count}</td>
                <td className={Number(p.flagged_count) > 0 ? 'bad' : 'good'}>{p.flagged_count}</td>
                <td className="muted small">
                  {p.drawn_relative_to_dose ?? 'unknown'}
                  {p.trained_within_48h ? ' · trained within 48h' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {panels.some((p) => p.trained_within_48h) && (
          <p className="muted small">
            One or more panels were drawn within 48 hours of a training session. AST and ALT are
            released by muscle, so those liver numbers may look worse than they are.
          </p>
        )}
      </div>
    </>
  );
}
